import { ensureOrgEmployees } from "../../../../../../services/useOrgData";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Box, Stack, IconButton, InputBase } from '@mui/material';
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import TopBar from "../../../../../../component/TopBar";
import SectionLoader from "../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../component/EmptyState";
import CoverageStrip, { CoverageLegend } from "../../../../../../component/leave/CoverageStrip";
import { StatusChip, PersonCell } from "../../../../../../component/listing";
import { toneColors } from "../../../../../../component/statusTone";
import {
  getAllLeaveRequests,
  getAllLeaveType,
  getLeaveCalendar,
  getLeaveSummary,
  getLeaveTrend,
  reviewLeaveRequest,
  cancelLeaveRequest,
} from "../../../../../../services/leave";
import TrendChart from "../../../../../../component/charts/TrendChart";
import ColumnChart from "../../../../../../component/charts/ColumnChart";

import { getAllEncashments } from "../../../../../../services/payroll";
import { buildLeaveMonth, daysInMonth, toDayString, formatLeaveRange, canCancel } from "../../../../../../utils/leaveMonth";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont, motion, radius } from "../../../../../../theme";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import ApplyLeave from "../../../employee-main/leave/ApplyLeave";
import DataTable from "../../../../../../component/DataTable";
import ActionDialog from "../../../../../../component/ActionDialog";

import { pressable } from "../../../../../../component/pressableProps";
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const STATUS_TONE = { Pending: "warning", Approved: "success", Rejected: "danger", Cancelled: "muted" };

// Row actions are a size below a page-level button: they sit inside a cell, and
// six of them stacked down a table should not out-shout the table itself.
const NUMERIC = { fontVariantNumeric: "tabular-nums" };

const rowActionBase = {
  borderRadius: "6px",
  fontSize: "0.8125rem",
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
  whiteSpace: "nowrap",
};
const rowActionSx = {
  approve: {
    ...rowActionBase,
    padding: "7px 14px",
    background: tokens.primary,
    color: tokens.card,
    border: "1px solid transparent",
    "&:hover": { background: tokens.primaryHover },
  },
  reject: {
    ...rowActionBase,
    padding: "7px 12px",
    background: "transparent",
    color: tokens.danger,
    border: `1px solid ${tokens.dangerBorder}`,
    "&:hover": { background: tokens.dangerTint },
  },
  view: {
    ...rowActionBase,
    padding: "7px 14px",
    background: "transparent",
    color: tokens.primary,
    border: `1px solid ${tokens.borderStrong}`,
    "&:hover": { background: tokens.surfaceSubtle },
  },
};

const panelSx = {
  background: tokens.card,
  border: `1px solid ${tokens.border}`,
  borderRadius: `${radius.card}px`,
  p: "1.25rem",
};

const Dash = () => (
  <Box component="span" aria-label="none" sx={{ color: tokens.textPlaceholder }}>—</Box>
);

/**
 * One of the seven counters.
 *
 * The book allows seven here and nowhere else — "it is only justified because
 * each one is a different question a different person asks" — which is exactly
 * why the ones at zero have to get out of the way. A zero is greyed rather than
 * coloured, so the eye lands on the counts that need something done.
 *
 * `ink` is a tone in its own right: "Days taken" is a fact rather than a state,
 * so it carries no status colour at all.
 */
const Tile = ({ label, value, hint, tone = "muted", active, onClick }) => {
  const { fg, bg } = toneColors(tone);
  const clickable = Boolean(onClick);
  const empty = !value;
  const figure = empty ? tokens.textInactive : tone === "ink" ? tokens.text : fg;
  return (
    <Box
      component={clickable ? "button" : "div"}
      type={clickable ? "button" : undefined}
      {...pressable(onClick)}
      aria-pressed={clickable ? active : undefined}
      sx={{
        flex: "1 1 8rem",
        minWidth: "8rem",
        textAlign: "left",
        font: "inherit",
        background: active ? bg : tokens.card,
        border: `1px solid ${active ? fg : tokens.border}`,
        borderRadius: "10px",
        p: "16px 18px",
        cursor: clickable ? "pointer" : "default",
        transition: `border-color 120ms ${motion.easing.standard}, background-color 120ms ${motion.easing.standard}`,
        "&:hover": clickable ? { borderColor: fg, background: bg } : undefined,
        "&:focus-visible": { outline: `2px solid ${tokens.primary}`, outlineOffset: "2px" },
      }}
    >
      <Box
        sx={{
          fontFamily: displayFont,
          fontWeight: 700,
          fontSize: "1.625rem",
          lineHeight: 1.2,
          mb: "4px",
          fontVariantNumeric: "tabular-nums",
          color: figure,
        }}
      >
        {value}
      </Box>
      {/* The label stays ink whatever the figure does: it is the question, and
          the question is as real at zero as it is at nine. */}
      <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.3, color: tokens.text }}>{label}</Box>
      {hint && (
        <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "3px" }}>{hint}</Box>
      )}
    </Box>
  );
};

const LeaveDashboard = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const today = useMemo(() => new Date(), []);

  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [requests, setRequests] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [empById, setEmpById] = useState({});
  const [headcount, setHeadcount] = useState(0);
  const [typeById, setTypeById] = useState({});
  const [pendingEncash, setPendingEncash] = useState(0);
  const [ledger, setLedger] = useState(null);
  const [liability, setLiability] = useState(null);
  const [requesting, setRequesting] = useState(false);
  // { type: "approve" | "reject", id } — the row whose decision is being taken
  const [deciding, setDeciding] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [filter, setFilter] = useState("All");
  const [selectedDay, setSelectedDay] = useState(null);
  const [search, setSearch] = useState("");

  const requestId = useRef(0);
  const { year, month } = cursor;
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();

  const fetchData = useCallback(async () => {
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    setFailed(false);
    const from = toDayString(year, month, 1);
    const to = toDayString(year, month, daysInMonth(year, month));

    const [reqRes, calRes, empRes, typeRes, encRes, sumRes, trendRes] = await Promise.all([
      getAllLeaveRequests(),
      getLeaveCalendar({ from, to }),
      ensureOrgEmployees(),
      getAllLeaveType(),
      getAllEncashments("pending"),
      getLeaveSummary({ from, to }),
      getLeaveTrend({ months: 12 }),
    ]);
    if (id !== requestId.current) return;
    setLedger(sumRes?.data || null);
    setLiability(trendRes?.data || null);

    if (reqRes?.success === false) {
      setFailed(true);
      notify.error(reqRes?.message || "Failed to load leave requests");
    }
    setRequests(Array.isArray(reqRes?.data) ? [...reqRes.data].reverse() : []);
    setCalendar(Array.isArray(calRes?.data) ? calRes.data : []);
    setPendingEncash(Array.isArray(encRes?.data) ? encRes.data.length : 0);

    const employees = Array.isArray(empRes) ? empRes : [];
    const em = {};
    employees.forEach((e) => { em[e._id] = e; });
    setEmpById(em);
    // coverage thresholds scale against the people who could actually be here
    setHeadcount(employees.filter((e) => e.isActive !== false).length);

    const tm = {};
    (typeRes?.data || []).forEach((t) => { tm[t._id] = t.name; });
    setTypeById(tm);
    setLoading(false);
  }, [year, month, notify]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // a different month invalidates any day selection made in the previous one
  useEffect(() => { setSelectedDay(null); }, [year, month]);

  const { days, summary } = useMemo(
    () => buildLeaveMonth({ year, month, entries: calendar, headcount, today }),
    [year, month, calendar, headcount, today]
  );

  // Pending approvals are deliberately NOT month-scoped: an approver needs the
  // whole queue, including requests for dates outside the month being viewed.
  const pendingAll = useMemo(() => requests.filter((r) => r.status === "Pending").length, [requests]);

  const counts = useMemo(() => {
    const inMonth = (r) => {
      if (!r.firstDay) return false;
      const first = toDayString(year, month, 1);
      const last = toDayString(year, month, daysInMonth(year, month));
      const rf = String(r.firstDay).slice(0, 10);
      const rl = String(r.lastDay || r.firstDay).slice(0, 10);
      return rf <= last && rl >= first;
    };
    const scoped = requests.filter(inMonth);
    return {
      All: scoped.length,
      Pending: scoped.filter((r) => r.status === "Pending").length,
      Approved: scoped.filter((r) => r.status === "Approved").length,
      Rejected: scoped.filter((r) => r.status === "Rejected").length,
      scoped,
    };
  }, [requests, year, month]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    // "Pending" shows the whole queue; every other view stays inside the month
    const base = filter === "Pending" ? requests.filter((r) => r.status === "Pending") : counts.scoped;
    return base.filter((r) => {
      const emp = empById[r.employeeId];
      const name = emp ? `${emp.fname || ""} ${emp.lname || ""}`.toLowerCase() : "";
      const matchesFilter = filter === "All" || r.status === filter;
      const matchesSearch = !q || name.includes(q);
      if (!selectedDay) return matchesFilter && matchesSearch;
      const covers = (r.days || []).some((d) => String(d.date).slice(0, 10) === selectedDay);
      return matchesFilter && matchesSearch && covers;
    });
  }, [requests, counts.scoped, filter, search, empById, selectedDay]);

  const step = (delta) =>
    setCursor((c) => {
      const next = new Date(Date.UTC(c.year, c.month + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
    });

  const clearScope = () => { setSelectedDay(null); setFilter("All"); setSearch(""); };
  const scoped = selectedDay || filter !== "All" || search.trim();

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar
        MainHeading={"Leave"}
        breadcrumbs={[{ label: "Leave", href: "" }, { label: "Dashboard", href: "" }]}
      />

      <Stack
        direction="row"
        alignItems="center"
        sx={{ mb: 2, flexWrap: "wrap", gap: "14px" }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {/* The month picker governs the whole screen, so it is drawn as the
              screen's title rather than as one control among several. */}
          <IconButton
            aria-label="Previous month"
            onClick={() => step(-1)}
            sx={{ width: "2.125rem", height: "2.125rem", border: `1px solid ${tokens.borderStrong}`, color: tokens.primary }}
          >
            <ChevronLeftIcon sx={{ fontSize: "1.125rem" }} />
          </IconButton>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 700, minWidth: "9rem", textAlign: "center", color: tokens.text }}>
            {MONTHS[month]} {year}
          </Box>
          <IconButton
            aria-label="Next month"
            onClick={() => step(1)}
            sx={{ width: "2.125rem", height: "2.125rem", border: `1px solid ${tokens.borderStrong}`, color: tokens.primary }}
          >
            <ChevronRightIcon sx={{ fontSize: "1.125rem" }} />
          </IconButton>
        </Stack>
        <Box sx={{ flex: 1 }} />
        {headcount > 0 && (
          <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>
            {headcount} active {headcount === 1 ? "employee" : "employees"}
          </Box>
        )}
        {/* The screen's one primary action. It files the viewer's own leave —
            an approver is somebody's report too — and lands in the same queue
            below, which is why the list is refreshed rather than left stale. */}
        <BtnPrimary onClick={() => setRequesting(true)} sx={{ borderRadius: "8px" }}>
          Request leave
        </BtnPrimary>
      </Stack>

      {loading ? (
        <SectionLoader minHeight="60vh" />
      ) : failed ? (
        <EmptyState variant="error" title="Couldn't load leave" onRetry={fetchData} />
      ) : (
        <Box>
          <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", mb: "1.25rem" }}>
            <Tile
              label="Away today"
              value={summary.onLeaveToday}
              hint={isCurrentMonth ? undefined : "current month only"}
              tone="info"
            />
            <Tile
              label="Awaiting approval"
              value={pendingAll}
              hint="all dates"
              tone="warning"
              active={filter === "Pending"}
              onClick={() => setFilter(filter === "Pending" ? "All" : "Pending")}
            />
            <Tile
              label="Approved"
              value={counts.Approved}
              tone="success"
              active={filter === "Approved"}
              onClick={counts.Approved ? () => setFilter(filter === "Approved" ? "All" : "Approved") : undefined}
            />
            <Tile
              label="Rejected"
              value={counts.Rejected}
              tone="danger"
              active={filter === "Rejected"}
              onClick={counts.Rejected ? () => setFilter(filter === "Rejected" ? "All" : "Rejected") : undefined}
            />
            <Tile
              label="Days taken"
              value={summary.dayCount}
              hint={summary.peopleAway ? `${summary.peopleAway} people` : undefined}
              tone="ink"
            />
            <Tile
              label="Thin cover"
              value={summary.strainedDays}
              hint={summary.peakCount ? `peak ${summary.peakCount} away` : undefined}
              tone={summary.strainedDays ? "warning" : "muted"}
            />
            <Tile
              label="Encashments"
              value={pendingEncash}
              hint="pending"
              tone="info"
              onClick={() => navigate("/app/leave-encashment")}
            />
          </Stack>

          <Box sx={{ ...panelSx, mb: "1.25rem" }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}
            >
              <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text }}>Team coverage</Box>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: "wrap" }}>
                <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                  Approved leave per day — select a day to filter the list
                </Box>
                {/* the strip and the team calendar are two renderings of one
                    question; switching between them shouldn't need the sidebar */}
                <Stack
                  direction="row"
                  sx={{ border: `1px solid ${tokens.border}`, borderRadius: "999px", p: "4px", background: tokens.card }}
                >
                  <Box
                    component="button"
                    type="button"
                    aria-pressed
                    sx={{
                      font: "inherit",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      border: "none",
                      cursor: "default",
                      borderRadius: "999px",
                      px: 1.5,
                      py: 0.5,
                      color: tokens.onPrimary,
                      background: tokens.primary,
                    }}
                  >
                    Strip
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    aria-pressed={false}
                    onClick={() => navigate("/app/leave-calendar")}
                    sx={{
                      font: "inherit",
                      fontSize: "0.8125rem",
                      fontWeight: 500,
                      border: "none",
                      cursor: "pointer",
                      borderRadius: "999px",
                      px: 1.5,
                      py: 0.5,
                      color: tokens.textMuted,
                      background: "transparent",
                      "&:hover": { color: tokens.text },
                      "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
                    }}
                  >
                    Calendar
                  </Box>
                </Stack>
              </Stack>
            </Stack>
            {summary.dayCount === 0 ? (
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, py: 2, textAlign: "center" }}>
                Nobody has approved leave in {MONTHS[month]} {year}.
              </Box>
            ) : (
              <>
                <CoverageStrip days={days} selectedDay={selectedDay} onSelectDay={setSelectedDay} />
                <CoverageLegend headcount={headcount} />
              </>
            )}
          </Box>

          {liability?.months?.length > 1 && (
            <Box sx={{ ...panelSx, mb: "1.25rem" }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ md: "flex-start" }}
                spacing={1.5}
                sx={{ mb: "0.75rem" }}
              >
                <Box>
                  <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text }}>Leave liability</Box>
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: 0.5 }}>
                    Days owed to staff at each month end
                    {liability.reconciles === false && " · ledger and balances disagree"}
                  </Box>
                </Box>
                <Stack direction="row" spacing={2.5} alignItems="flex-start">
                  <Box>
                    <Box sx={{ fontFamily: displayFont, fontSize: "1.75rem", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                      {liability.currentLiability}
                    </Box>
                    <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>days owed now</Box>
                  </Box>
                  {(() => {
                    const m = liability.months;
                    const change = m[m.length - 1].liability - m[0].liability;
                    return (
                      <Box>
                        <Box
                          sx={{
                            fontFamily: displayFont, fontWeight: 800, fontSize: "1.75rem",
                            fontVariantNumeric: "tabular-nums",
                            color: change > 0 ? tokens.warningText : tokens.text,
                          }}
                        >
                          {change > 0 ? "+" : ""}{Math.round(change)}
                        </Box>
                        <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>
                          since {m[0].label}
                        </Box>
                      </Box>
                    );
                  })()}
                </Stack>
              </Stack>

              {/* one series, so no legend — the title already names what is plotted */}
              <TrendChart
                points={liability.months.map((m) => ({ x: m.key, label: m.label }))}
                series={[
                  {
                    key: "liability",
                    label: "Days owed",
                    color: tokens.accent,
                    values: liability.months.map((m) => m.liability),
                  },
                ]}
                height={170}
                yMax={Math.max(10, Math.ceil(Math.max(...liability.months.map((m) => m.liability)) * 1.15))}
                formatValue={(v) => `${Math.round(v)}d`}
              />

              {/* the same months underneath: what drove the curve each period.
                  Separate panel rather than a second axis — these are per-month
                  magnitudes an order smaller than the cumulative line. */}
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${tokens.border}` }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  sx={{ mb: 0.5, gap: 1, flexWrap: "wrap" }}
                >
                  <Box sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>What moved it each month</Box>
                  <Stack direction="row" spacing={2}>
                    {[
                      { label: "Accrued", color: tokens.accent },
                      { label: "Taken", color: tokens.danger },
                    ].map((s) => (
                      <Stack key={s.label} direction="row" alignItems="center" spacing={0.625}>
                        <Box sx={{ width: "10px", height: "10px", borderRadius: "6px", background: s.color }} />
                        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{s.label}</Box>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
                <ColumnChart
                  points={liability.months.map((m) => ({ x: m.key, label: m.label }))}
                  series={[
                    {
                      key: "accrued",
                      label: "Accrued",
                      color: tokens.accent,
                      values: liability.months.map((m) => m.accrued),
                    },
                    {
                      key: "taken",
                      label: "Taken",
                      color: tokens.danger,
                      values: liability.months.map((m) => m.taken),
                    },
                  ]}
                  height={150}
                  formatValue={(v) => `${Math.round(v * 100) / 100}d`}
                />
              </Box>
            </Box>
          )}

          {ledger && (
            <Box sx={{ ...panelSx, mb: "1.25rem" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}
              >
                <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text }}>Balance and accrual</Box>
                <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                  Movements in {MONTHS[month]} {year} · balances as they stand today
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} sx={{ alignItems: "stretch" }}>
                <Box
                  sx={{
                    minWidth: "12rem",
                    borderRight: { md: `1px solid ${tokens.border}` },
                    pr: { md: 2.5 },
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: displayFont, fontWeight: 800, fontSize: "2.125rem",
                      fontVariantNumeric: "tabular-nums",
                      color: tokens.text,
                    }}
                  >
                    {ledger.liability.totalDays}
                  </Box>
                  <Box sx={{ fontSize: "0.875rem", color: tokens.text }}>days owed to staff</Box>
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: 0.5 }}>
                    {ledger.liability.paidDays} of them paid leave
                  </Box>
                  {ledger.integrity.drifted > 0 ? (
                    <Box sx={{ fontSize: "0.8125rem", color: tokens.warningText, mt: 1 }}>
                      {ledger.integrity.drifted} of {ledger.integrity.pairsChecked} balances
                      don't match the ledger
                    </Box>
                  ) : (
                    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: 1 }}>
                      All {ledger.integrity.pairsChecked} balances trace to the ledger
                    </Box>
                  )}
                </Box>

                <Box sx={{ flex: 1, overflowX: "auto" }}>
                  <DataTable
                    rows={ledger.byType}
                    getRowId={(t) => String(t.leaveType.id)}
                    tableSx={{ minWidth: "26rem" }}
                    columns={[
                      {
                        key: "type",
                        label: "Leave type",
                        render: (t) => (
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Box>{t.leaveType.name}</Box>
                            {!t.leaveType.isPaid && (
                              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>unpaid</Box>
                            )}
                          </Stack>
                        ),
                      },
                      {
                        key: "accrued",
                        label: "Accrued",
                        align: "right",
                        cellSx: NUMERIC,
                        // A zero movement is greyed rather than coloured, the
                        // same rule the counters above follow.
                        render: (t) => (
                          <Box component="span" sx={{ color: t.accrued ? toneColors("success").fg : tokens.textInactive }}>
                            {t.accrued ? `+${t.accrued}` : "—"}
                          </Box>
                        ),
                      },
                      {
                        key: "taken",
                        label: "Taken",
                        align: "right",
                        cellSx: NUMERIC,
                        render: (t) => (
                          <Box component="span" sx={{ color: t.taken ? toneColors("danger").fg : tokens.textInactive }}>
                            {t.taken ? `−${t.taken}` : "—"}
                          </Box>
                        ),
                      },
                      {
                        key: "net",
                        label: "Net",
                        align: "right",
                        cellSx: { ...NUMERIC, fontWeight: 600 },
                        render: (t) => (t.net > 0 ? `+${t.net}` : t.net),
                      },
                      { key: "outstanding", label: "Outstanding", align: "right", cellSx: NUMERIC, render: (t) => t.outstanding },
                    ]}
                  />
                </Box>
              </Stack>
            </Box>
          )}

          <Box sx={panelSx}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ md: "center" }}
              spacing={1.5}
              sx={{ mb: "1rem" }}
            >
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: "wrap" }}>
                <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text }}>
                  {filter === "Pending" ? "Awaiting approval" : "Leave requests"}
                </Box>
                <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                  {selectedDay
                    ? `on ${selectedDay}`
                    : filter === "Pending"
                    ? "across all dates"
                    : `in ${MONTHS[month]} ${year}`}
                </Box>
                {scoped && (
                  <Box
                    component="button"
                    type="button"
                    onClick={clearScope}
                    sx={{
                      font: "inherit",
                      fontSize: "0.8125rem",
                      color: tokens.accent,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      p: 0,
                      "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
                    }}
                  >
                    Clear
                  </Box>
                )}
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  border: `1px solid ${tokens.border}`,
                  borderRadius: "10px",
                  px: 1.5,
                  height: "2.5rem",
                  minWidth: "14rem",
                }}
              >
                <SearchIcon sx={{ fontSize: "1.125rem", color: tokens.textMuted }} />
                <InputBase
                  placeholder="Search employee"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ fontSize: "0.8125rem", flex: 1 }}
                />
              </Stack>
            </Stack>

            <DataTable
              rows={rows}
              empty={{
                title: scoped ? "No matching requests" : "No leave requests",
                description: scoped
                  ? "Nothing matches the current filters."
                  : `No leave was requested for ${MONTHS[month]} ${year}.`,
                actionLabel: scoped ? "Clear filters" : undefined,
                onAction: scoped ? clearScope : undefined,
              }}
              onRowClick={(r) => navigate(`/app/leave-requests-details/${r._id}`)}
              tableSx={{ width: "100%", tableLayout: "fixed" }}
              columns={[
                {
                  key: "employee",
                  label: "Employee",
                  width: "26%",
                  render: (r) => {
                    const emp = empById[r.employeeId];
                    return (
                      <PersonCell
                        name={emp ? `${emp.fname || ""} ${emp.lname || ""}`.trim() : "Unknown"}
                        secondary={emp?.designation}
                        src={emp?.profileImage}
                      />
                    );
                  },
                },
                { key: "type", label: "Leave type", render: (r) => typeById[r.leaveType] || <Dash /> },
                {
                  key: "dates",
                  label: "Dates",
                  width: "10.5rem",
                  cellSx: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
                  render: (r) =>
                    formatLeaveRange(
                      r.firstDay ? String(r.firstDay).slice(0, 10) : null,
                      r.lastDay ? String(r.lastDay).slice(0, 10) : (r.firstDay ? String(r.firstDay).slice(0, 10) : null)
                    ) || <Dash />,
                },
                {
                  key: "days",
                  label: "Days",
                  width: "5rem",
                  align: "right",
                  cellSx: { fontVariantNumeric: "tabular-nums" },
                  render: (r) => r?.days?.length || <Dash />,
                },
                {
                  key: "status",
                  label: "Status",
                  width: "7rem",
                  render: (r) => <StatusChip label={r.status} tone={STATUS_TONE[r.status]} />,
                },
                {
                  key: "actions",
                  label: "",
                  width: "11.5rem",
                  headSx: { px: "0.5rem" },
                  cellSx: { px: "0.5rem", whiteSpace: "nowrap" },
                  // Every row carries an action, because an empty cell reads as
                  // a broken one — and the decision offered depends on whether
                  // one is still open.
                  render: (r) => (
                    <Stack direction="row" sx={{ gap: "8px", justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                      {r.status === "Pending" ? (
                        <>
                          <Box component="button" type="button" onClick={() => setDeciding({ type: "approve", id: r._id })} sx={rowActionSx.approve}>
                            Approve
                          </Box>
                          <Box component="button" type="button" onClick={() => setDeciding({ type: "reject", id: r._id })} sx={rowActionSx.reject}>
                            Reject
                          </Box>
                        </>
                      ) : (
                        <>
                          <Box component="button" type="button" onClick={() => navigate(`/app/leave-requests-details/${r._id}`)} sx={rowActionSx.view}>
                            View
                          </Box>
                          {canCancel(r) && (
                            <Box component="button" type="button" onClick={() => setCancelling(r)} sx={rowActionSx.reject}>
                              Cancel
                            </Box>
                          )}
                        </>
                      )}
                    </Stack>
                  ),
                },
              ]}
            />
          </Box>
        </Box>
      )}
      {/* Both decisions go through the shared dialog rather than firing straight
          from the row. Approving is not undoable from here, and a rejection
          requires a reason — dropping that to save a click would leave people
          told no with nothing to act on. */}
      <ActionDialog
        type={deciding?.type === "reject" ? "reject" : "approve"}
        open={Boolean(deciding)}
        onClose={() => setDeciding(null)}
        reviewApi={reviewLeaveRequest}
        id={deciding?.id}
        text={deciding?.type === "reject" ? "Reject this leave request?" : "Approve this leave request?"}
        onDone={fetchData}
      />

      <ActionDialog
        type="reject"
        open={Boolean(cancelling)}
        onClose={() => setCancelling(null)}
        // The dialog posts (form, id); cancel takes (id, form), and it wants a
        // reason for the same purpose a rejection does.
        reviewApi={(form, id) => cancelLeaveRequest(id, { reason: form?.reviewerComment })}
        id={cancelling?._id}
        text="Cancel this approved leave?"
        onDone={fetchData}
      />

      <ApplyLeave
        open={requesting}
        onClose={() => setRequesting(false)}
        // Only on an actual submission: onClose fires on cancel too, and
        // refetching the whole month because somebody opened and shut a sheet
        // is a spinner for nothing.
        onSubmitted={fetchData}
      />
    </Box>
  );
};

export default LeaveDashboard;
